/**
 * 방 선택 화면의 문서를 상담 상태에서 만든다.
 *
 * 프로덕션의 `RoomObservingFlowAdapter.buildPickFlow` + `buildObservingPickerSections`
 * 에 대응한다. 이 화면만은 Firestore 에 저장된 문서가 없다 — 어떤 방이 있는지는 상담마다
 * 다르므로 어댑터가 매번 만든다. 에디터에 남아 있는 `room-record-flow` 초안은 아이콘도
 * 섹션도 없는 옛 스케치라 그대로 띄우면 실제 화면과 다른 물건이 된다.
 *
 * 그래서 "무엇이 상담마다 달라지는가"(방 목록)와 "무엇이 고정인가"(섹션 구성·이모지·
 * 문구·CTA)를 여기서 갈라 둔다. 고정된 쪽은 문서로 읽히고, 달라지는 쪽만 인자로 받는다.
 */

/** 방 하나. 프로덕션 `SketchingSessionRoom` + 옵저빙 세션 상태를 합친 최소 형태. */
export interface RoomPickEntry {
  id: string;
  label: string;
  /** 프로덕션 룸 타입 (`LIVING_ROOM` / `ROOM` / `BALCONY` …). */
  type: string;
  /** 카드 아래 보조 텍스트 (부속 시설 등). */
  description?: string;
  /** 짐 기록·영상이 끝난 방. 카드에 완료 배지가 붙는다. */
  done?: boolean;
}

/** 프로덕션 `ROOM_ICON_BY_TYPE`. */
const ICON_BY_TYPE: Record<string, string> = {
  ENTRANCE: '🚪',
  LIVING_ROOM: '🛋️',
  KITCHEN: '🍳',
  // 🛌(자는 사람 + 침대) — 단순 침대 🛏️ 보다 다수 환경에서 컬러 글리프로 안정적이다.
  ROOM: '🛌',
  BATH_ROOM: '🚿',
  BALCONY: '🪟',
  RESIDENCE_STORAGE: '📦',
  OTHER_SPACE: '📦',
};

const PRIMARY_TYPES = new Set(['LIVING_ROOM', 'KITCHEN', 'BATH_ROOM']);
const ROOM_TYPES = new Set(['ROOM']);
/** 현관은 어느 섹션에도 넣지 않는다 — 규칙이 자동으로 먼저 기록시키므로 고를 일이 없다. */
const EXCLUDED_TYPES = new Set(['ENTRANCE']);

const SECTION_TITLES: Record<'primary' | 'rooms' | 'additional', string> = {
  primary: '주요공간',
  rooms: '방',
  additional: '추가공간',
};

export const ROOM_PICK_FLOW_ID = 'room-observing-pick-flow';
export const ROOM_PICK_QUESTION_ID = 'roomToRecord';

/**
 * 프로덕션과 같은 3 섹션("주요공간 / 방 / 추가공간")으로 나눈다.
 * 빈 섹션은 내보내지 않는다 — 시각적 노이즈를 줄이기 위함.
 */
function sectionsOf(entries: RoomPickEntry[]): Array<{
  id: string;
  title: string;
  items: Array<Record<string, unknown>>;
}> {
  const bucket = (e: RoomPickEntry): 'primary' | 'rooms' | 'additional' =>
    PRIMARY_TYPES.has(e.type) ? 'primary' : ROOM_TYPES.has(e.type) ? 'rooms' : 'additional';

  const visible = entries.filter((e) => !EXCLUDED_TYPES.has(e.type));

  return (['primary', 'rooms', 'additional'] as const)
    .map((id) => ({
      id,
      title: SECTION_TITLES[id],
      items: visible
        .filter((e) => bucket(e) === id)
        .map((e) => ({
          id: e.id,
          title: e.label,
          icon: ICON_BY_TYPE[e.type] ?? '📦',
          ...(e.description ? { description: e.description } : {}),
        })),
    }))
    .filter((s) => s.items.length > 0);
}

/**
 * 완료된 방의 배지용 인스턴스.
 *
 * navigation 모드의 ItemPicker 는 인스턴스가 하나라도 있는 카드를 완료로 그린다.
 * 프로덕션 `entriesToDoneInstances` 와 같은 방식이다.
 */
export function roomPickDoneInstances(
  entries: RoomPickEntry[],
): Array<{ instanceId: string; itemId: string; answers: Record<string, unknown> }> {
  return entries
    .filter((e) => e.done)
    .map((e) => ({ instanceId: `done_${e.id}`, itemId: e.id, answers: {} }));
}

/**
 * 방 선택 v1 FlowDocument.
 *
 * `mode: 'navigation'` 인 이유: 카드를 고르면 이 문서 안에서 답이 채워지는 게 아니라
 * 짐 기록이라는 다른 화면이 열린다. 그 화면이 끝나야 방 하나가 완료된다.
 */
export function buildRoomPickFlow(entries: RoomPickEntry[]): Record<string, unknown> {
  return {
    schemaVersion: 1,
    id: ROOM_PICK_FLOW_ID,
    initial: 'pick',
    steps: {
      pick: {
        kind: 'questions',
        questions: [
          {
            id: ROOM_PICK_QUESTION_ID,
            layout: 'L2',
            title: '기록할 방을 선택해주세요.',
            input: {
              kind: 'item-picker',
              required: false,
              multiple: false,
              mode: 'navigation',
              requireAllItems: true,
              reviewTitle: '기록할 방',
              reviewButtonLabel: '완료',
              sections: sectionsOf(entries),
            },
          },
        ],
        next: { kind: 'terminal', target: 'done' },
      },
    },
    terminals: { done: { kind: 'placeholder-auto' } },
  };
}
